import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import ChartContainer from '@/components/Chart/ChartContainer';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, Callout, Card, Disclosure, StatusBadge } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';
import { ideaLabPreviewRetryDelay, isIdeaLabPreviewBusy, retryIdeaLabPreview } from './ideaLabPreviewRetry';
import { getDate } from '@einhaus/meteoric-helpers';
import type { ChartMarker, ChartTextItem } from '@einhaus/project314-types';
import {
    formatEasternUnix,
    formatHorizonLabel,
    formatIdeaLabTraceMarkerLabel,
    formatSignedDollars,
    formatSignedPercent,
    formatNativeRawMoney,
    deriveIdeaLabChartIndicators,
    getRunStatusTone,
    LENS_LABELS,
    parsePositiveInt,
    percentToneClass,
    RUN_STATUS_LABELS,
    type IdeaLabEntryReviewCase
} from './ideaLabUtils';

const PAGE_SIZE = 50;

const unixToMarketDateTime = (unix: number): string => getDate({ date: unix, format: 'ymdhms', timezone: 'America/New_York' });

const toInputDateTime = (value: string): string => value.replace(' ', 'T').slice(0, 16);
const fromInputDateTime = (value: string): string => `${value.replace('T', ' ')}:00`;

const caseKey = (entry: IdeaLabEntryReviewCase): string =>
    `${entry.entityType}:${entry.entityId}:${entry.entryId}:${entry.decisionAtUnix}:${entry.executionAtUnix}`;

const marker = (
    unix: number | null | undefined,
    text: string,
    options: Pick<ChartMarker, 'color' | 'position' | 'shape' | 'size'>
): ChartMarker | null => (unix == null ? null : { dateTime: unixToMarketDateTime(unix), text, ...options });

const uniqueMarkers = (markers: Array<ChartMarker | null>): ChartMarker[] => {
    const seen = new Set<string>();
    return markers.filter((candidate): candidate is ChartMarker => {
        if (!candidate) return false;
        const key = `${candidate.dateTime}:${candidate.text}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const windowForCase = (
    entry: IdeaLabEntryReviewCase,
    run: { startDateTime: string; endDateTime: string; primaryDuration: string }
): { startDateTime: string; endDateTime: string } => {
    const anchor = entry.originEventAtUnix ?? entry.signalBarAtUnix ?? entry.decisionAtUnix;
    const end = entry.resolvedEndAtUnix ?? entry.executionAtUnix;
    const [beforeDays, afterDays] = run.primaryDuration.endsWith('m')
        ? [3, 3]
        : run.primaryDuration === '1d'
          ? [90, 30]
          : run.primaryDuration === '1w'
            ? [365, 90]
            : [730, 180];
    const startDateTime = unixToMarketDateTime(anchor - beforeDays * 86_400);
    const endDateTime = unixToMarketDateTime(end + afterDays * 86_400);
    return {
        startDateTime: startDateTime < run.startDateTime ? run.startDateTime : startDateTime,
        endDateTime: endDateTime > run.endDateTime ? run.endDateTime : endDateTime
    };
};

const IdeaLabEntryReview = () => {
    const { runId: runIdParam } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const runId = parsePositiveInt(runIdParam ?? null);
    const captureMode = searchParams.get('capture') === '1';
    const requestedMarket = searchParams.get('entityType');
    const marketFilter =
        requestedMarket === 'Asset' || requestedMarket === 'AssetIndex' || requestedMarket === 'Cryptocurrency'
            ? requestedMarket
            : undefined;
    const initialReplayEntity =
        searchParams.get('entity') ??
        (searchParams.get('entityType') && searchParams.get('entityId')
            ? `${searchParams.get('entityType')}:${searchParams.get('entityId')}`
            : '');
    const [variantId, setVariantId] = useState<number | null>(() => parsePositiveInt(searchParams.get('variantId')));
    const [lens, setLens] = useState<'underlying' | 'option' | 'futures' | null>(() => {
        const value = searchParams.get('lens');
        return value === 'underlying' || value === 'option' || value === 'futures' ? value : null;
    });
    const [horizonSeconds, setHorizonSeconds] = useState<number | null>(() => parsePositiveInt(searchParams.get('horizonSeconds')));
    const [entityFilter, setEntityFilter] = useState(initialReplayEntity);
    const [offset, setOffset] = useState(0);
    const [selectedCaseKey, setSelectedCaseKey] = useState<string | null>(null);
    const entryListRef = useRef<HTMLDivElement>(null);
    const [replayEntity, setReplayEntity] = useState(initialReplayEntity);
    const [replayStart, setReplayStart] = useState(searchParams.get('startDateTime') ?? '');
    const [replayEnd, setReplayEnd] = useState(searchParams.get('endDateTime') ?? '');
    const [activeReplay, setActiveReplay] = useState<{
        entityType: 'Asset' | 'AssetIndex' | 'Cryptocurrency';
        entityId: number;
        startDateTime: string;
        endDateTime: string;
    } | null>(null);
    const [underlyingSettled, setUnderlyingSettled] = useState(false);
    const [optionSettled, setOptionSettled] = useState(false);
    const [preserveTargetedReplay, setPreserveTargetedReplay] = useState(
        initialReplayEntity !== '' && searchParams.has('startDateTime') && searchParams.has('endDateTime')
    );

    const runQuery = useAppQuery(
        { ...trpc.ideaLab.getRun.queryOptions({ runId: runId ?? 0 }), enabled: runId !== null },
        { fallbackErrorMessage: 'Failed to load the Idea Lab run' }
    );
    const run = runQuery.data ?? null;
    const variantQuery = useAppQuery(
        { ...trpc.ideaLab.getVariant.queryOptions({ variantId: variantId ?? 0 }), enabled: variantId !== null },
        { fallbackErrorMessage: 'Failed to load the Idea Lab variant' }
    );
    const chartIndicators = useMemo(
        () => deriveIdeaLabChartIndicators(variantQuery.data?.authoringPayload),
        [variantQuery.data?.authoringPayload]
    );
    const chartIndicatorLabel = chartIndicators.map((indicator) => `${indicator.type.toUpperCase()} ${indicator.period}`).join(' · ');
    const availableLenses = useMemo(
        () => [
            ...new Set(
                (run?.horizons ?? [])
                    .filter((cell) => marketFilter !== 'Cryptocurrency' || cell.lens === 'underlying')
                    .map((cell) => cell.lens)
            )
        ],
        [run?.horizons, marketFilter]
    );
    const availableHorizons = useMemo(
        () =>
            [...new Set((run?.horizons ?? []).filter((cell) => cell.lens === lens).map((cell) => cell.horizonSeconds))].sort(
                (left, right) => left - right
            ),
        [lens, run?.horizons]
    );

    useEffect(() => {
        if (!run) return;
        if (!run.variants.some((variant) => variant.variantId === variantId)) setVariantId(run.variants[0]?.variantId ?? null);
        if (lens === null || !availableLenses.includes(lens)) setLens(availableLenses[0] ?? null);
        const firstEntity = run.entities.find(
            (entity) =>
                (marketFilter === undefined || entity.entityType === marketFilter) &&
                (lens !== 'option' || entity.entityType !== 'Cryptocurrency')
        );
        if (replayEntity === '' && firstEntity) setReplayEntity(`${firstEntity.entityType}:${firstEntity.entityId}`);
    }, [availableLenses, lens, marketFilter, replayEntity, run, variantId]);

    useEffect(() => {
        if (availableHorizons.length > 0 && !availableHorizons.includes(horizonSeconds ?? -1)) {
            setHorizonSeconds(availableHorizons[0] ?? null);
        }
    }, [availableHorizons, horizonSeconds]);

    useEffect(() => {
        if (!run || !preserveTargetedReplay || activeReplay !== null) return;
        const [entityType, rawId] = initialReplayEntity.split(':');
        const entityId = parsePositiveInt(rawId);
        if (
            (entityType !== 'Asset' && entityType !== 'AssetIndex' && entityType !== 'Cryptocurrency') ||
            entityId === null ||
            !replayStart ||
            !replayEnd
        )
            return;
        if (!run.entities.some((candidate) => candidate.entityType === entityType && candidate.entityId === entityId)) return;
        setActiveReplay({ entityType, entityId, startDateTime: replayStart, endDateTime: replayEnd });
    }, [activeReplay, initialReplayEntity, preserveTargetedReplay, replayEnd, replayStart, run]);

    const parsedFilter = useMemo<{ entityType: 'Asset' | 'AssetIndex' | 'Cryptocurrency'; entityId: number } | null>(() => {
        const [entityType, rawId] = entityFilter.split(':');
        const entityId = parsePositiveInt(rawId);
        return (entityType === 'Asset' || entityType === 'AssetIndex' || entityType === 'Cryptocurrency') && entityId !== null
            ? { entityType, entityId }
            : null;
    }, [entityFilter]);

    const casesQuery = useAppQuery(
        {
            ...trpc.ideaLab.listEntryReviewCases.queryOptions({
                runId: runId ?? 0,
                variantId: variantId ?? 0,
                lens: lens ?? 'underlying',
                horizonSeconds: horizonSeconds ?? 1,
                ...(parsedFilter ?? (marketFilter === undefined ? {} : { entityType: marketFilter })),
                offset,
                limit: PAGE_SIZE
            }),
            enabled: run?.status === 'completed' && variantId !== null && lens !== null && horizonSeconds !== null
        },
        { fallbackErrorMessage: 'Failed to load entry-level review evidence' }
    );
    const cases = casesQuery.data?.cases ?? [];
    const selectedCase = cases.find((entry) => caseKey(entry) === selectedCaseKey) ?? cases[0] ?? null;

    useEffect(() => {
        if (!selectedCase || !run || preserveTargetedReplay) return;
        const reviewWindow = windowForCase(selectedCase, run);
        const entity = `${selectedCase.entityType}:${selectedCase.entityId}`;
        setSelectedCaseKey(caseKey(selectedCase));
        setReplayEntity(entity);
        setReplayStart(reviewWindow.startDateTime);
        setReplayEnd(reviewWindow.endDateTime);
        setActiveReplay({
            entityType: selectedCase.entityType,
            entityId: selectedCase.entityId,
            ...reviewWindow
        });
    }, [preserveTargetedReplay, run, selectedCase?.entryId, selectedCase?.decisionAtUnix]);

    useEffect(() => {
        if (!activeReplay || variantId === null || lens === null || horizonSeconds === null) return;
        const next = new URLSearchParams(searchParams);
        next.set('variantId', String(variantId));
        next.set('lens', lens);
        next.set('horizonSeconds', String(horizonSeconds));
        next.set('entity', `${activeReplay.entityType}:${activeReplay.entityId}`);
        next.set('startDateTime', activeReplay.startDateTime);
        next.set('endDateTime', activeReplay.endDateTime);
        setSearchParams(next, { replace: true });
    }, [activeReplay, horizonSeconds, lens, setSearchParams, variantId]);

    const previewQuery = useAppQuery(
        {
            ...trpc.ideaLab.previewEntryReviewWindow.queryOptions({
                runId: runId ?? 0,
                variantId: variantId ?? 0,
                entityType: activeReplay?.entityType ?? 'Asset',
                entityId: activeReplay?.entityId ?? 0,
                startDateTime: activeReplay?.startDateTime ?? '1970-01-01 00:00:00',
                endDateTime: activeReplay?.endDateTime ?? '1970-01-01 00:00:01'
            }),
            enabled: run !== null && variantId !== null && activeReplay !== null,
            retry: retryIdeaLabPreview,
            retryDelay: ideaLabPreviewRetryDelay,
            staleTime: Infinity
        },
        { fallbackErrorMessage: 'Failed to replay the entry logic for this window' }
    );
    const preview = previewQuery.data ?? null;

    const entityQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityType: activeReplay?.entityType ?? 'Asset',
            entityId: activeReplay?.entityId ?? 0
        }),
        enabled: activeReplay !== null
    });
    const entity = entityQuery.data?.entity ?? null;
    const authoritativeCase =
        !preserveTargetedReplay &&
        selectedCaseKey !== null &&
        selectedCase &&
        activeReplay &&
        selectedCase.entityId === activeReplay.entityId &&
        selectedCase.entityType === activeReplay.entityType
            ? selectedCase
            : null;
    const selectedOptionId = authoritativeCase?.option.optionId ?? null;
    const optionQuery = useAppQuery({
        ...trpc.entity.fetchOptions.queryOptions({
            options:
                selectedOptionId === null ||
                activeReplay === null ||
                (activeReplay.entityType !== 'Asset' && activeReplay.entityType !== 'AssetIndex')
                    ? []
                    : [{ oId: selectedOptionId, eId: activeReplay.entityId, eType: activeReplay.entityType }]
        }),
        enabled: selectedOptionId !== null && activeReplay !== null
    });
    const option = optionQuery.data?.options[0] ?? null;

    useEffect(() => {
        setUnderlyingSettled(false);
        setOptionSettled(false);
    }, [activeReplay?.entityType, activeReplay?.entityId, activeReplay?.startDateTime, activeReplay?.endDateTime, selectedOptionId]);

    const generationDiagnostics = preview?.result.diagnostics?.generationDiagnostics ?? null;
    const traceEvents = useMemo(
        () => generationDiagnostics?.sampleFrames.flatMap((frame) => frame.traceEvents) ?? [],
        [generationDiagnostics]
    );
    const underlyingMarkers = useMemo(() => {
        const replayEntries = preview?.result.entries ?? [];
        const replayEntryMarkers = replayEntries.flatMap((entry) => [
            marker(entry.originatingEventAtUnixTs, 'Setup', { color: 'purple', position: 'belowBar', shape: 'circle', size: 1 }),
            marker(entry.signalBarTimestamp, 'Signal', { color: 'orange', position: 'belowBar', shape: 'circle', size: 1 }),
            marker(entry.decisionTimestamp, 'Decision', { color: 'yellow', position: 'aboveBar', shape: 'circle', size: 1 }),
            marker(entry.executionTimestamp ?? entry.timestamp, `${entry.direction === 'long' ? 'Long' : 'Short'} entry`, {
                color: entry.direction === 'long' ? 'green' : 'red',
                position: entry.direction === 'long' ? 'belowBar' : 'aboveBar',
                shape: entry.direction === 'long' ? 'arrowUp' : 'arrowDown',
                size: 2
            })
        ]);
        const traceMarkers = traceEvents
            .filter((event) => event.stepId?.includes('::step:'))
            .map((event) =>
                marker(event.observedAtUnixTs, formatIdeaLabTraceMarkerLabel(event), {
                    color: 'purple',
                    position: 'inBar',
                    shape: 'circle',
                    size: 1
                })
            );
        const hasMatchingReplayTime = (
            persistedUnix: number | null | undefined,
            readReplayUnix: (entry: (typeof replayEntries)[number]) => number | null | undefined
        ): boolean => persistedUnix != null && replayEntries.some((entry) => readReplayUnix(entry) === persistedUnix);
        const outcomeLabel = authoritativeCase
            ? `${authoritativeCase.futures ? 'Native futures ' : ''}${formatHorizonLabel(authoritativeCase.horizonSeconds)} outcome ${formatSignedPercent(authoritativeCase.returnPercent)}`
            : '';
        return uniqueMarkers([
            ...replayEntryMarkers,
            ...traceMarkers,
            marker(
                hasMatchingReplayTime(authoritativeCase?.originEventAtUnix, (entry) => entry.originatingEventAtUnixTs)
                    ? null
                    : authoritativeCase?.originEventAtUnix,
                'Persisted setup',
                {
                    color: 'purple',
                    position: 'belowBar',
                    shape: 'circle',
                    size: 1
                }
            ),
            marker(
                hasMatchingReplayTime(authoritativeCase?.signalBarAtUnix, (entry) => entry.signalBarTimestamp)
                    ? null
                    : authoritativeCase?.signalBarAtUnix,
                'Persisted signal',
                {
                    color: 'orange',
                    position: 'belowBar',
                    shape: 'circle',
                    size: 1
                }
            ),
            marker(
                hasMatchingReplayTime(authoritativeCase?.decisionAtUnix, (entry) => entry.decisionTimestamp)
                    ? null
                    : authoritativeCase?.decisionAtUnix,
                'Persisted decision',
                {
                    color: 'yellow',
                    position: 'aboveBar',
                    shape: 'circle',
                    size: 1
                }
            ),
            marker(
                hasMatchingReplayTime(authoritativeCase?.executionAtUnix, (entry) => entry.executionTimestamp ?? entry.timestamp)
                    ? null
                    : authoritativeCase?.executionAtUnix,
                `${authoritativeCase?.direction === 'short' ? 'Short' : 'Long'} persisted entry`,
                {
                    color: authoritativeCase?.direction === 'short' ? 'red' : 'green',
                    position: authoritativeCase?.direction === 'short' ? 'aboveBar' : 'belowBar',
                    shape: authoritativeCase?.direction === 'short' ? 'arrowDown' : 'arrowUp',
                    size: 2
                }
            ),
            marker(authoritativeCase?.resolvedEndAtUnix, outcomeLabel, {
                color: 'blue',
                position: 'aboveBar',
                shape: 'square',
                size: 2
            })
        ]);
    }, [authoritativeCase, preview?.result.entries, traceEvents]);
    const traceConsoleItems = useMemo<ChartTextItem[]>(
        () =>
            traceEvents.map((event) => ({
                dateTime: unixToMarketDateTime(event.observedAtUnixTs),
                text: `${event.kind}: ${event.message}`
            })),
        [traceEvents]
    );
    const optionMarkers = useMemo(
        () =>
            uniqueMarkers([
                marker(authoritativeCase?.option.quoteAtUnix, 'Selection quote', {
                    color: 'yellow',
                    position: 'aboveBar',
                    shape: 'circle',
                    size: 1
                }),
                marker(authoritativeCase?.option.actualEntryAtUnix, 'Option entry', {
                    color: 'green',
                    position: 'belowBar',
                    shape: 'arrowUp',
                    size: 2
                }),
                marker(authoritativeCase?.resolvedEndAtUnix, `${formatHorizonLabel(authoritativeCase?.horizonSeconds ?? 0)} outcome`, {
                    color: 'blue',
                    position: 'aboveBar',
                    shape: 'square',
                    size: 2
                }),
                marker(authoritativeCase?.option.expirationAtUnix, 'Expiration', {
                    color: 'red',
                    position: 'aboveBar',
                    shape: 'circle',
                    size: 1
                })
            ]),
        [authoritativeCase]
    );

    const replayRequest = useMemo(() => {
        const [entityType, rawId] = replayEntity.split(':');
        const entityId = parsePositiveInt(rawId);

        if (
            (entityType !== 'Asset' && entityType !== 'AssetIndex' && entityType !== 'Cryptocurrency') ||
            entityId === null ||
            !replayStart ||
            !replayEnd
        )
            return null;
        if (!(replayStart < replayEnd)) return null;

        return { entityType, entityId, startDateTime: replayStart, endDateTime: replayEnd } as const;
    }, [replayEnd, replayEntity, replayStart]);

    const submitReplay = () => {
        if (replayRequest === null) throw new Error('Replay requested without a valid symbol and window');

        setPreserveTargetedReplay(true);
        setSelectedCaseKey(null);
        setActiveReplay(replayRequest);
    };

    const selectCase = (entry: IdeaLabEntryReviewCase) => {
        setPreserveTargetedReplay(false);
        setSelectedCaseKey(caseKey(entry));
    };
    const selectedIndex = selectedCaseKey === null ? -1 : cases.findIndex((entry) => caseKey(entry) === selectedCaseKey);
    const canGoPrevious = selectedIndex > 0 || offset > 0;
    const canGoNext = (selectedIndex >= 0 && selectedIndex < cases.length - 1) || casesQuery.data?.hasMore === true;
    const goPrevious = () => {
        if (selectedIndex > 0) selectCase(cases[selectedIndex - 1]!);
        else if (offset > 0) setOffset(Math.max(0, offset - PAGE_SIZE));
    };
    const goNext = () => {
        if (selectedIndex >= 0 && selectedIndex < cases.length - 1) selectCase(cases[selectedIndex + 1]!);
        else if (casesQuery.data?.hasMore) setOffset(offset + PAGE_SIZE);
    };

    // Keep the selected entry visible as the list moves under the keyboard or the buttons.
    useEffect(() => {
        if (selectedCaseKey === null) return;

        entryListRef.current?.querySelector(`[data-case-key="${CSS.escape(selectedCaseKey)}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [selectedCaseKey]);

    const renderFailureReason =
        previewQuery.error instanceof Error
            ? 'preview-error'
            : entityQuery.error instanceof Error
              ? 'entity-error'
              : optionQuery.error instanceof Error
                ? 'option-error'
                : null;
    const renderReady =
        entity !== null && preview !== null && underlyingSettled && (selectedOptionId === null || (option !== null && optionSettled));

    if (runId === null) {
        return (
            <UserLayout title="Idea Lab Visual Validator - Project 314">
                <Callout variant="error">Invalid run id.</Callout>
            </UserLayout>
        );
    }

    const hasEntryList = !captureMode && run?.status === 'completed';
    const selectClassName = 'mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100';
    const inputClassName = 'mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100';

    return (
        <UserLayout containerSize="1600px" title="Idea Lab Visual Validator - Project 314">
            <div
                data-render-failure-reason={renderFailureReason ?? undefined}
                data-render-ready={renderReady ? 'true' : 'false'}
                id="ideaLabEntryReviewCaptureFrame"
            >
                {!captureMode && (
                    <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                            <Link className="text-xs text-zinc-500 hover:text-zinc-300" to={`/user/ideaLab/run/${runId}`}>
                                ← {run?.label ?? `Run #${runId}`}
                            </Link>
                            <h1 className="text-2xl font-semibold text-zinc-100">
                                Visual Validator
                                <span className="ml-2 align-middle text-sm font-normal text-zinc-500">run #{runId}</span>
                            </h1>
                            <p className="mt-1 text-sm text-zinc-400">
                                Pick an entry to replay the pinned entry logic around it and compare the chart with the persisted outcome.
                            </p>
                        </div>
                        {run && (
                            <StatusBadge status={run.status} tone={getRunStatusTone(run.status)}>
                                {RUN_STATUS_LABELS[run.status]}
                            </StatusBadge>
                        )}
                    </div>
                )}

                {runQuery.isLoading ? (
                    <DataLoadingSpinner text="Loading run..." />
                ) : runQuery.error instanceof Error ? (
                    <Callout variant="error">{runQuery.error.message}</Callout>
                ) : run ? (
                    <div className="space-y-4">
                        {!captureMode && run.status !== 'completed' && (
                            <Callout variant="info">
                                Targeted replay is available now. Persisted entry and outcome evidence appears after the run completes.
                            </Callout>
                        )}

                        {hasEntryList && (
                            <div className="grid gap-3 rounded-lg border border-zinc-800 bg-[#1a1a1a] p-3 sm:grid-cols-2 lg:grid-cols-4">
                                <label className="text-xs text-zinc-400">
                                    Variant
                                    <select
                                        className={selectClassName}
                                        value={variantId ?? ''}
                                        onChange={(event) => {
                                            setVariantId(Number(event.target.value));
                                            setOffset(0);
                                            setSelectedCaseKey(null);
                                        }}
                                    >
                                        {run.variants.map((variant) => (
                                            <option key={variant.variantId} value={variant.variantId}>
                                                #{variant.variantId} · {variant.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="text-xs text-zinc-400">
                                    Lens
                                    <select
                                        className={selectClassName}
                                        value={lens ?? ''}
                                        onChange={(event) => {
                                            const next = event.target.value;
                                            if (next === 'underlying' || next === 'option' || next === 'futures') setLens(next);
                                            setOffset(0);
                                        }}
                                    >
                                        {availableLenses.map((value) => (
                                            <option key={value} value={value}>
                                                {LENS_LABELS[value]}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="text-xs text-zinc-400">
                                    Horizon
                                    <select
                                        className={selectClassName}
                                        value={horizonSeconds ?? ''}
                                        onChange={(event) => {
                                            setHorizonSeconds(Number(event.target.value));
                                            setOffset(0);
                                        }}
                                    >
                                        {availableHorizons.map((value) => (
                                            <option key={value} value={value}>
                                                {formatHorizonLabel(value)}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="text-xs text-zinc-400">
                                    Symbol
                                    <select
                                        className={selectClassName}
                                        value={entityFilter}
                                        onChange={(event) => {
                                            setEntityFilter(event.target.value);
                                            setOffset(0);
                                        }}
                                    >
                                        <option value="">{marketFilter ? `All ${marketFilter} symbols` : 'All symbols'}</option>
                                        {run.entities
                                            .filter(
                                                (item) =>
                                                    (marketFilter === undefined || item.entityType === marketFilter) &&
                                                    (lens !== 'option' || item.entityType !== 'Cryptocurrency')
                                            )
                                            .map((item) => (
                                                <option
                                                    key={`${item.entityType}:${item.entityId}`}
                                                    value={`${item.entityType}:${item.entityId}`}
                                                >
                                                    {item.symbol}
                                                </option>
                                            ))}
                                    </select>
                                </label>
                            </div>
                        )}

                        <div className={hasEntryList ? 'grid items-start gap-4 xl:grid-cols-[380px_minmax(0,1fr)]' : ''}>
                            {hasEntryList && (
                                <section
                                    aria-label="Entries"
                                    className="flex flex-col rounded-lg border border-zinc-800 bg-[#1a1a1a] xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)]"
                                >
                                    <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
                                        <h2 className="text-sm font-semibold text-zinc-200">Entries</h2>
                                        <span className="text-[11px] text-zinc-500">↑ ↓ or j k to move</span>
                                    </div>
                                    <div
                                        className="max-h-[60vh] min-h-0 flex-1 overflow-y-auto focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 xl:max-h-none"
                                        ref={entryListRef}
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (event.key === 'ArrowDown' || event.key === 'j') {
                                                event.preventDefault();
                                                goNext();
                                            } else if (event.key === 'ArrowUp' || event.key === 'k') {
                                                event.preventDefault();
                                                goPrevious();
                                            }
                                        }}
                                    >
                                        <table className="w-full text-left text-xs">
                                            <thead className="sticky top-0 bg-[#131416] text-zinc-500">
                                                <tr>
                                                    <th className="px-3 py-2 font-medium">Symbol</th>
                                                    <th className="px-2 py-2 font-medium">Entry (ET)</th>
                                                    <th className="px-2 py-2 text-right font-medium">Outcome</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {cases.map((entry) => {
                                                    const key = caseKey(entry);
                                                    const isSelected = key === selectedCaseKey;

                                                    return (
                                                        <tr
                                                            aria-selected={isSelected}
                                                            className={`cursor-pointer border-t border-zinc-800 ${isSelected ? 'bg-blue-500/15' : 'hover:bg-zinc-800/40'}`}
                                                            data-case-key={key}
                                                            key={key}
                                                            onClick={() => selectCase(entry)}
                                                        >
                                                            <td
                                                                className={`border-l-2 px-3 py-1.5 font-medium ${isSelected ? 'border-blue-400 text-white' : 'border-transparent text-blue-300'}`}
                                                            >
                                                                <button
                                                                    className="text-left focus-visible:rounded focus-visible:outline-2 focus-visible:outline-blue-400"
                                                                    type="button"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                        selectCase(entry);
                                                                    }}
                                                                >
                                                                    {entry.symbol}
                                                                </button>
                                                                {entry.direction === 'short' && (
                                                                    <span className="ml-1 text-[10px] uppercase text-zinc-500">short</span>
                                                                )}
                                                            </td>
                                                            <td className="whitespace-nowrap px-2 py-1.5 text-zinc-300">
                                                                {formatEasternUnix(entry.executionAtUnix)}
                                                            </td>
                                                            <td className="whitespace-nowrap px-2 py-1.5 text-right">
                                                                {entry.outcomeState === 'mature' ? (
                                                                    <span className={percentToneClass(entry.returnPercent)}>
                                                                        {formatSignedPercent(entry.returnPercent, 2)}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-zinc-500">{entry.outcomeState}</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {!casesQuery.isLoading && cases.length === 0 && (
                                                    <tr>
                                                        <td className="px-3 py-6 text-center text-zinc-500" colSpan={3}>
                                                            No persisted entries match these filters. Replay a custom window to inspect a
                                                            near miss.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        {casesQuery.isLoading && (
                                            <div className="p-3">
                                                <DataLoadingSpinner text="Loading entries..." />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between border-t border-zinc-800 px-3 py-2">
                                        <Button
                                            disabled={offset === 0}
                                            size="sm"
                                            variant="outline-dark"
                                            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                        >
                                            Previous page
                                        </Button>
                                        <span className="text-[11px] text-zinc-500">
                                            {cases.length === 0 ? 'No entries' : `Entries ${offset + 1}–${offset + cases.length}`}
                                        </span>
                                        <Button
                                            disabled={!casesQuery.data?.hasMore}
                                            size="sm"
                                            variant="outline-dark"
                                            onClick={() => setOffset(offset + PAGE_SIZE)}
                                        >
                                            Next page
                                        </Button>
                                    </div>
                                </section>
                            )}

                            <div className="min-w-0 space-y-4">
                                {!captureMode && (
                                    <div className="rounded-lg border border-zinc-800 bg-[#1a1a1a] p-3">
                                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                            <div className="min-w-0 text-sm">
                                                {authoritativeCase ? (
                                                    <span className="text-zinc-200">
                                                        <span className="font-semibold text-white">{authoritativeCase.symbol}</span>{' '}
                                                        {authoritativeCase.direction} entry{' '}
                                                        {formatEasternUnix(authoritativeCase.executionAtUnix)} ·{' '}
                                                        {formatHorizonLabel(authoritativeCase.horizonSeconds)} outcome{' '}
                                                        {authoritativeCase.outcomeState === 'mature' ? (
                                                            <span className={percentToneClass(authoritativeCase.returnPercent)}>
                                                                {formatSignedPercent(authoritativeCase.returnPercent, 2)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-zinc-500">{authoritativeCase.outcomeState}</span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-zinc-400">
                                                        Custom replay window: the exact pinned artifact and frozen run settings, including
                                                        windows where no entry fired.
                                                    </span>
                                                )}
                                            </div>
                                            {hasEntryList && (
                                                <div className="flex gap-2">
                                                    <Button disabled={!canGoPrevious} size="sm" variant="outline-dark" onClick={goPrevious}>
                                                        ← Previous entry
                                                    </Button>
                                                    <Button disabled={!canGoNext} size="sm" variant="outline-dark" onClick={goNext}>
                                                        Next entry →
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
                                            <label className="text-xs text-zinc-400">
                                                Replay symbol
                                                <select
                                                    className={selectClassName}
                                                    value={replayEntity}
                                                    onChange={(event) => setReplayEntity(event.target.value)}
                                                >
                                                    {run.entities
                                                        .filter(
                                                            (item) =>
                                                                (marketFilter === undefined || item.entityType === marketFilter) &&
                                                                (lens !== 'option' || item.entityType !== 'Cryptocurrency')
                                                        )
                                                        .map((item) => (
                                                            <option
                                                                key={`${item.entityType}:${item.entityId}`}
                                                                value={`${item.entityType}:${item.entityId}`}
                                                            >
                                                                {item.symbol}
                                                            </option>
                                                        ))}
                                                </select>
                                            </label>
                                            <label className="text-xs text-zinc-400">
                                                From (ET)
                                                <input
                                                    className={inputClassName}
                                                    max={toInputDateTime(run.endDateTime)}
                                                    min={toInputDateTime(run.startDateTime)}
                                                    type="datetime-local"
                                                    value={replayStart ? toInputDateTime(replayStart) : ''}
                                                    onChange={(event) => setReplayStart(fromInputDateTime(event.target.value))}
                                                />
                                            </label>
                                            <label className="text-xs text-zinc-400">
                                                To (ET)
                                                <input
                                                    className={inputClassName}
                                                    max={toInputDateTime(run.endDateTime)}
                                                    min={toInputDateTime(run.startDateTime)}
                                                    type="datetime-local"
                                                    value={replayEnd ? toInputDateTime(replayEnd) : ''}
                                                    onChange={(event) => setReplayEnd(fromInputDateTime(event.target.value))}
                                                />
                                            </label>
                                            <Button
                                                disabled={replayRequest === null}
                                                isLoading={previewQuery.isFetching}
                                                title={
                                                    replayRequest === null
                                                        ? 'Pick a symbol and a window whose start precedes its end'
                                                        : undefined
                                                }
                                                variant="primary"
                                                onClick={submitReplay}
                                            >
                                                Replay window
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {previewQuery.error instanceof Error && <Callout variant="error">{previewQuery.error.message}</Callout>}
                                {entity && activeReplay && preview ? (
                                    <Card
                                        subtitle={`${run.primaryDuration} · ${activeReplay.startDateTime} → ${activeReplay.endDateTime} ET · ${preview.result.entries.length} generated entries${chartIndicatorLabel ? ` · ${chartIndicatorLabel}` : ''}`}
                                        title={`${preview.symbol} underlying`}
                                    >
                                        <div className="min-h-[640px]">
                                            <ChartContainer
                                                consoleItemsOverride={traceConsoleItems}
                                                duration={run.primaryDuration}
                                                endDateTime={activeReplay.endDateTime}
                                                entity={entity}
                                                hideComparisonSearch={captureMode}
                                                hideControls={captureMode}
                                                hideSettingsPanel
                                                indicators={chartIndicators}
                                                markersOverride={underlyingMarkers}
                                                shouldIncludeVolumeSeries
                                                startDateTime={activeReplay.startDateTime}
                                                onInitialRenderSettled={() => setUnderlyingSettled(true)}
                                            />
                                        </div>
                                    </Card>
                                ) : (
                                    !captureMode &&
                                    activeReplay &&
                                    !(previewQuery.error instanceof Error) && (
                                        <DataLoadingSpinner
                                            text={
                                                isIdeaLabPreviewBusy(previewQuery.failureReason)
                                                    ? 'Waiting for chart preview capacity. Retrying shortly...'
                                                    : 'Replaying the entry logic...'
                                            }
                                        />
                                    )
                                )}

                                {authoritativeCase?.futures && (
                                    <Card
                                        title="Native futures outcome"
                                        subtitle={`Configuration #${authoritativeCase.futures.configurationId} · ${authoritativeCase.futures.reasonCode || authoritativeCase.outcomeState}`}
                                    >
                                        <div className="flex flex-wrap gap-4 text-sm">
                                            <span>ETF signal close {authoritativeCase.futures.signalPrice}</span>
                                            <span>
                                                Product #{authoritativeCase.futures.productId ?? '—'} · dated contract #
                                                {authoritativeCase.futures.contractId ?? '—'}
                                            </span>
                                            <span>
                                                {authoritativeCase.futures.quantity ?? '—'} native contracts · point value{' '}
                                                {formatNativeRawMoney(authoritativeCase.futures.pointValueRaw)}
                                            </span>
                                            <span>Opening source artifact #{authoritativeCase.futures.openingBarArtifactId ?? '—'}</span>
                                            <span>Native modeled opening {authoritativeCase.futures.entryPrice ?? '—'}</span>
                                            <span>Native modeled close {authoritativeCase.endPrice ?? '—'}</span>
                                            <span>Net cash {formatNativeRawMoney(authoritativeCase.futures.netCashMoneyRaw)}</span>
                                            <span>Fees {formatNativeRawMoney(authoritativeCase.futures.feesMoneyRaw)}</span>
                                            <span>
                                                Opening reserve {formatNativeRawMoney(authoritativeCase.futures.openingReserveMoneyRaw)}
                                            </span>
                                            <span>
                                                Return on reserve{' '}
                                                {formatSignedPercent(authoritativeCase.futures.returnOnAllocatedCapitalPercent)}
                                            </span>
                                        </div>
                                    </Card>
                                )}
                                {option && entity && activeReplay && authoritativeCase && (
                                    <Card
                                        subtitle={`Separate premium scale · ${authoritativeCase.option.lifecycle || authoritativeCase.option.reasonCode || 'selection evidence'}`}
                                        title={`${option.symbol} option`}
                                    >
                                        <div className="mb-3 grid gap-2 text-xs text-zinc-400 sm:grid-cols-4">
                                            <span>Strike {authoritativeCase.option.strike ?? '—'}</span>
                                            <span>DTE {authoritativeCase.option.entryDteDays?.toFixed(1) ?? '—'}</span>
                                            <span>Spread {formatSignedPercent(authoritativeCase.option.spreadPercent)}</span>
                                            <span>Net P&amp;L {formatSignedDollars(authoritativeCase.option.netProfit)}</span>
                                        </div>
                                        <div className="min-h-[560px]">
                                            <ChartContainer
                                                duration="1m"
                                                endDateTime={activeReplay.endDateTime}
                                                entity={entity}
                                                hideComparisonSearch={captureMode}
                                                hideControls={captureMode}
                                                hideSettingsPanel
                                                markersOverride={optionMarkers}
                                                option={option}
                                                shouldIncludeVolumeSeries={false}
                                                startDateTime={activeReplay.startDateTime}
                                                onInitialRenderSettled={() => setOptionSettled(true)}
                                            />
                                        </div>
                                    </Card>
                                )}

                                {!captureMode && generationDiagnostics && (
                                    <Disclosure
                                        badge={
                                            <span className="text-xs font-normal text-zinc-500">
                                                {generationDiagnostics.totalFrameCount} frames · {generationDiagnostics.selectedActionCount}{' '}
                                                actions · {generationDiagnostics.generatedEntryCount} entries
                                            </span>
                                        }
                                        title="Generation evidence"
                                        variant="card"
                                    >
                                        <p className="mb-3 text-xs text-zinc-500">
                                            Runtime trace events are observed sequence evidence. Persisted entry and outcome markers remain
                                            the authoritative run facts.
                                        </p>
                                        <div className="max-h-[420px] overflow-auto rounded border border-zinc-800">
                                            <table className="w-full text-left text-xs">
                                                <thead className="sticky top-0 bg-zinc-900 text-zinc-500">
                                                    <tr>
                                                        <th className="px-3 py-2">Observed</th>
                                                        <th className="px-3 py-2">Truth</th>
                                                        <th className="px-3 py-2">Matched nodes / stages</th>
                                                        <th className="px-3 py-2">Runtime events</th>
                                                        <th className="px-3 py-2">Result</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {generationDiagnostics.sampleFrames.map((frame) => (
                                                        <tr
                                                            className="border-t border-zinc-800 align-top"
                                                            key={`${frame.frameId}:${frame.observedAtUnixTs}`}
                                                        >
                                                            <td className="whitespace-nowrap px-3 py-2 text-zinc-300">
                                                                {formatEasternUnix(frame.observedAtUnixTs)}
                                                            </td>
                                                            <td className="px-3 py-2 text-zinc-300">{frame.truth}</td>
                                                            <td className="px-3 py-2 text-zinc-400">
                                                                {[...frame.matchedNodeIds, ...frame.matchedStageIds].join(', ') || '—'}
                                                            </td>
                                                            <td className="px-3 py-2 text-zinc-400">
                                                                {frame.traceEvents.map((event) => event.message).join(' · ') || '—'}
                                                            </td>
                                                            <td className="px-3 py-2 text-zinc-300">
                                                                {frame.generatedEntryId
                                                                    ? `entry ${frame.generatedEntryId}`
                                                                    : (frame.skippedReason ?? frame.selectedActionKind ?? '—')}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Disclosure>
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </UserLayout>
    );
};

export default IdeaLabEntryReview;
